import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { RequestedFields } from '@wix/ambassador-asa-service-facade-v1-service/types';
import { isValidMeeting } from '../../entities/Meeting';
import { isValidProviderStatus } from '../../entities/ProviderStatus';
import { isValidService } from '../../entities/Service';
import { SlotsType } from '../../entities/ServiceSlots';
import { pagingMetadataToCursorResponse } from '../../utils/PagingUtils';
import { BaseClient } from '../BaseClient';
export class ServicesFacadeClientBase extends BaseClient {
  constructor() {
    super(...arguments);
    _defineProperty(this, "queryServicesImpl", void 0);
    _defineProperty(this, "searchServicesImpl", void 0);
    _defineProperty(this, "getServiceImpl", void 0);
    _defineProperty(this, "getInstalledProvidersOptInStatusImpl", void 0);
    _defineProperty(this, "listAvailabilityImpl", void 0);
    _defineProperty(this, "listMeetingsImpl", void 0);
    _defineProperty(this, "listCalendarsImpl", void 0);
    _defineProperty(this, "updateProviderOptStatusImpl", void 0);
    _defineProperty(this, "getMeetingImpl", void 0);
  }
  getServiceNameForLogs() {
    return 'ServicesFacadeClient';
  }
  async listCalendars(providerId, paging) {
    try {
      const request = {
        provider: providerId,
        paging
      };
      for (var _len = arguments.length, extraParam = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
        extraParam[_key - 2] = arguments[_key];
      }
      const {
        calendars,
        pagingMetadata
      } = await this.request(this.listCalendarsImpl(request), ...extraParam);
      return {
        items: calendars || [],
        ...pagingMetadataToCursorResponse(pagingMetadata)
      };
    } catch (error) {
      this.logError('listCalenders', 'Failed to list calendars', {
        providerId,
        paging
      }, error);
      throw error;
    }
  }
  async listMeetings(req) {
    try {
      const {
        providerId,
        dateInterval,
        paging,
        calendarId
      } = req;
      const request = {
        provider: providerId,
        calendarId,
        paging,
        startDate: dateInterval.startDate,
        endDate: dateInterval.endDate
      };
      for (var _len2 = arguments.length, extraParam = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
        extraParam[_key2 - 1] = arguments[_key2];
      }
      const {
        meetings,
        pagingMetadata
      } = await this.request(this.listMeetingsImpl(request), ...extraParam);
      return {
        items: (meetings == null ? void 0 : meetings.map(meeting => this.convertMeetingApiToMeeting(meeting, providerId))) || [],
        ...pagingMetadataToCursorResponse(pagingMetadata)
      };
    } catch (error) {
      this.logError('listMeetings', 'Failed to list meetings', {
        req
      }, error);
      throw error;
    }
  }
  async queryServices() {
    for (var _len3 = arguments.length, extraParam = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
      extraParam[_key3] = arguments[_key3];
    }
    try {
      const providers = await this.getOptedInProviders(...extraParam);
      const results = await Promise.all(providers.map(provider => this.request(this.queryServicesImpl({
        provider,
        query: {}
      }), ...extraParam)));
      const services = results.reduce((acc, curr) => [...acc, ...(curr.services ?? [])], []).map(this.convertToEntity);
      const hasNext = results.some(result => {
        var _result$pagingMetadat;
        return (_result$pagingMetadat = result.pagingMetadata) == null ? void 0 : _result$pagingMetadat.hasNext;
      });
      this.logInfo('queryServices', 'result', {
        services,
        hasNext
      });
      return {
        services,
        hasNext
      };
    } catch (error) {
      this.logError('queryServices', 'Failed to query services', {}, error);
      throw error;
    }
  }
  async queryServicesByProvider(providerId, query) {
    try {
      for (var _len4 = arguments.length, extraParam = new Array(_len4 > 2 ? _len4 - 2 : 0), _key4 = 2; _key4 < _len4; _key4++) {
        extraParam[_key4 - 2] = arguments[_key4];
      }
      const {
        services,
        pagingMetadata
      } = await this.request(this.queryServicesImpl({
        provider: providerId,
        query
      }), ...extraParam);
      if (!services) {
        throw new Error('Received invalid services');
      }
      const convertedServices = services.map(this.convertToEntity);
      this.logInfo('queryServicesByProvider', 'result', {
        services,
        pagingMetadata
      });
      return {
        items: convertedServices,
        ...pagingMetadataToCursorResponse(pagingMetadata)
      };
    } catch (error) {
      this.logError('queryServicesByProvider', 'Failed to query services by provider', {
        providerId,
        query
      }, error);
      throw error;
    }
  }
  async searchServices(expression) {
    for (var _len5 = arguments.length, extraParam = new Array(_len5 > 1 ? _len5 - 1 : 0), _key5 = 1; _key5 < _len5; _key5++) {
      extraParam[_key5 - 1] = arguments[_key5];
    }
    try {
      const providers = await this.getOptedInProviders(...extraParam);
      const results = await Promise.all(providers.map(provider => this.request(this.searchServicesImpl({
        provider,
        search: {
          search: {
            expression,
            fuzzy: true
          }
        }
      }), ...extraParam)));
      const services = results.reduce((acc, curr) => [...acc, ...(curr.services ?? [])], []).map(this.convertToEntity);
      const hasNext = results.some(result => {
        var _result$pagingMetadat2;
        return (_result$pagingMetadat2 = result.pagingMetadata) == null ? void 0 : _result$pagingMetadat2.hasNext;
      });
      this.logInfo('searchServices', 'result', {
        services,
        hasNext
      });
      return {
        services,
        hasNext
      };
    } catch (error) {
      this.logError('searchServices', 'Failed to search services', {
        expression
      }, error);
      throw error;
    }
  }
  async getService(provider, id) {
    try {
      for (var _len6 = arguments.length, extraParam = new Array(_len6 > 2 ? _len6 - 2 : 0), _key6 = 2; _key6 < _len6; _key6++) {
        extraParam[_key6 - 2] = arguments[_key6];
      }
      const {
        service
      } = await this.request(this.getServiceImpl({
        provider,
        serviceId: id
      }), ...extraParam);
      const convertedService = this.convertToEntity(service);
      this.logInfo('getService', 'result', {
        services: convertedService
      });
      return convertedService;
    } catch (error) {
      this.logError('getService', 'Failed to get service', {
        provider,
        id
      }, error);
      throw error;
    }
  }
  async getServiceWithExternalData(provider, id) {
    try {
      const request = {
        provider,
        serviceId: id,
        fields: [RequestedFields.EXTERNAL_SERVICE]
      };
      for (var _len7 = arguments.length, extraParam = new Array(_len7 > 2 ? _len7 - 2 : 0), _key7 = 2; _key7 < _len7; _key7++) {
        extraParam[_key7 - 2] = arguments[_key7];
      }
      const {
        service
      } = await this.request(this.getServiceImpl(request), ...extraParam);
      const convertedService = {
        ...this.convertToEntity(service),
        externalData: (service == null ? void 0 : service.externalService) ?? {}
      };
      this.logInfo('getServiceWithExternalData', 'result', {
        convertedService,
        service
      });
      return convertedService;
    } catch (error) {
      this.logError('getServiceWithExternalData', 'Failed to get service', {
        provider,
        id
      }, error);
      throw error;
    }
  }
  async getMeeting(request) {
    try {
      for (var _len8 = arguments.length, extraParam = new Array(_len8 > 1 ? _len8 - 1 : 0), _key8 = 1; _key8 < _len8; _key8++) {
        extraParam[_key8 - 1] = arguments[_key8];
      }
      const {
        meeting
      } = await this.request(this.getMeetingImpl(request), ...extraParam);
      if (!meeting) {
        throw new Error('Meeting not found');
      }
      return this.convertMeetingApiToMeeting(meeting, request.provider);
    } catch (error) {
      this.logError('getMeeting', 'Failed to get meeting', {
        request
      }, error);
      throw error;
    }
  }
  async getInstalledProvidersOptInStatus() {
    try {
      for (var _len9 = arguments.length, extraParam = new Array(_len9), _key9 = 0; _key9 < _len9; _key9++) {
        extraParam[_key9] = arguments[_key9];
      }
      const {
        providers
      } = await this.request(this.getInstalledProvidersOptInStatusImpl({}), ...extraParam);
      if (!providers) {
        throw new Error('No providers found');
      }
      this.logInfo('getInstalledProvidersOptInStatus', 'result', {
        providers
      });
      const convertedProviders = providers.map(provider => ({
        provider: provider.provider,
        optInStatus: provider.optInStatues
      }));
      if (convertedProviders.every(isValidProviderStatus)) {
        return convertedProviders;
      }
      throw new Error('Invalid providers data', {
        cause: {
          providers
        }
      });
    } catch (error) {
      this.logError('getInstalledProvidersOptInStatus', 'Failed to get installed providers opt in status', {}, error);
      throw error;
    }
  }
  async getOptedInProviders() {
    const providers = await this.getInstalledProvidersOptInStatus(...arguments);
    return providers.filter(provider => provider.optInStatus).map(provider => provider.provider);
  }
  async updateProviderOptStatus(providerId, optInStatus) {
    try {
      for (var _len0 = arguments.length, extraParam = new Array(_len0 > 2 ? _len0 - 2 : 0), _key0 = 2; _key0 < _len0; _key0++) {
        extraParam[_key0 - 2] = arguments[_key0];
      }
      await this.request(this.updateProviderOptStatusImpl({
        provider: providerId,
        optIn: optInStatus
      }), ...extraParam);
    } catch (error) {
      this.logError('setProviderOptStatus', 'Failed to set provider opt status', {
        providerId,
        optInStatus
      }, error);
      throw error;
    }
  }
  async listAvailability(calendarId, provider, timeRange, providerState) {
    try {
      for (var _len1 = arguments.length, extraParam = new Array(_len1 > 4 ? _len1 - 4 : 0), _key1 = 4; _key1 < _len1; _key1++) {
        extraParam[_key1 - 4] = arguments[_key1];
      }
      const {
        slotType,
        slots
      } = await this.request(this.listAvailabilityImpl({
        calendarId,
        provider,
        state: providerState,
        startDate: new Date(timeRange.startTimestamp),
        endDate: new Date(timeRange.endTimestamp)
      }), ...extraParam);
      const serviceSlots = this.convertToServiceSlots({
        slotType,
        slots
      });
      this.logInfo('listAvailability', 'result', {
        serviceSlots
      });
      return serviceSlots;
    } catch (error) {
      this.logError('listAvailability', 'Failed to query availability', {
        calendarId,
        provider,
        timeRange,
        providerState
      }, error);
      throw error;
    }
  }
  convertToEntity(service) {
    try {
      if (!service) {
        throw new Error('Invalid service', {
          cause: {
            service
          }
        });
      }
      const {
        default: defaultService,
        categoryName,
        ...restService
      } = service;
      const convertedService = {
        ...restService,
        defaultService,
        categoryName: categoryName ?? undefined
      };
      if (isValidService(convertedService)) {
        return convertedService;
      }
      throw new Error('Invalid Service data', {
        cause: {
          service
        }
      });
    } catch (error) {
      this.logError('convertToEntity', 'Failed to convert service', {
        service
      }, error);
      throw error;
    }
  }
  convertSlotTypeToSlotsType(slotType) {
    switch (slotType) {
      case 'FREE':
        return SlotsType.FREE;
      case 'BUSY':
        return SlotsType.BUSY;
      default:
        throw new Error('Failed to convert slotType', {
          cause: {
            slotType
          }
        });
    }
  }
  convertSlotApiToSlot(slot) {
    try {
      const {
        startDate,
        endDate,
        externalSlot
      } = slot;
      if (!startDate) {
        throw new Error('Invalid slot start date', {
          cause: {
            startDate
          }
        });
      }
      if (!endDate) {
        throw new Error('Invalid slot end date', {
          cause: {
            endDate
          }
        });
      }
      const convertedSlot = {
        startTimestamp: startDate.getTime(),
        endTimestamp: endDate.getTime(),
        externalSlot: externalSlot ?? undefined
      };
      return convertedSlot;
    } catch (error) {
      this.logError('convertSlotApiToSlot', 'Failed to convert slot', {
        slot
      }, error);
      throw error;
    }
  }
  convertToServiceSlots(_ref) {
    let {
      slotType,
      slots
    } = _ref;
    try {
      if (!slotType) {
        throw new Error('Invalid slotType', {
          cause: {
            slotType
          }
        });
      }
      if (!slots) {
        throw new Error('Invalid slots', {
          cause: {
            slots
          }
        });
      }
      const serviceSlots = {
        type: this.convertSlotTypeToSlotsType(slotType),
        slots: slots.map(this.convertSlotApiToSlot)
      };
      return serviceSlots;
    } catch (error) {
      this.logError('convertToServiceSlots', 'Failed to convert service slots', {
        slotType,
        slots
      }, error);
      throw error;
    }
  }
  convertMeetingApiToMeeting(meeting, providerId) {
    try {
      var _meeting$slot, _meeting$slot2;
      const convertedMeeting = {
        id: meeting.id,
        providerId,
        calendarId: meeting.calendarId,
        serviceId: meeting.serviceId,
        slot: {
          startDate: (_meeting$slot = meeting.slot) == null ? void 0 : _meeting$slot.startDate,
          endDate: (_meeting$slot2 = meeting.slot) == null ? void 0 : _meeting$slot2.endDate
        },
        externalMeeting: meeting.externalMeeting
      };
      if (isValidMeeting(convertedMeeting)) {
        return convertedMeeting;
      }
      throw new Error('Invalid Meeting data', {
        cause: {
          meeting
        }
      });
    } catch (error) {
      this.logError('convertMeetingApiToMeeting', 'Failed to convert meeting', {
        meeting,
        providerId
      }, error);
      throw error;
    }
  }
}
//# sourceMappingURL=ServicesFacadeClientBase.js.map